import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  buildPerformanceSeries,
  buildPerformanceStreamRange,
} from '@/utils/campaignPerformanceSeries';
import type { PerformanceGranularity } from '@/utils/performanceChartMetrics';
import type { DateRange } from '@/api/reportDateRanges';

function normalizeHeatmapTimezone(value: string | undefined | null): string {
  const requested = (value || '').trim();
  if (!requested) return 'UTC';
  if (requested === 'Asia/Calcutta') return 'Asia/Kolkata';
  return requested;
}

export function campaignPerformanceQueryKey(
  profileId: string,
  campaignId: string,
  range: DateRange,
  granularity: PerformanceGranularity
): readonly unknown[] {
  return [
    'campaign-performance-series',
    profileId,
    campaignId,
    range.startDate,
    range.endDate,
    granularity,
  ] as const;
}

export function useCampaignPerformanceSeries({
  profileId,
  campaignId,
  range,
  granularity,
  enabled = true,
}: {
  profileId: string;
  campaignId: string;
  range: DateRange;
  granularity: PerformanceGranularity;
  enabled?: boolean;
}) {
  const browserTimezone = normalizeHeatmapTimezone(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const streamRange = buildPerformanceStreamRange(range);

  return useQuery({
    queryKey: campaignPerformanceQueryKey(profileId, campaignId, range, granularity),
    queryFn: async () => {
      const res = await adsApi.getStreamHeatmap(profileId, {
        startTime: streamRange.startIso,
        endTime: streamRange.endIso,
        timezone: browserTimezone,
        days: streamRange.dayCount,
        view: 'normal',
        campaignIds: [campaignId],
      });
      if (!res.success) throw new Error('Failed to load campaign performance data');
      const payload = (res.data as Record<string, unknown>) ?? {};
      const series = buildPerformanceSeries(payload, range, granularity);
      return {
        ...series,
        timezone: String(payload.timezone || browserTimezone),
        rowCount: typeof payload.rowCount === 'number' ? payload.rowCount : 0,
      };
    },
    enabled: enabled && Boolean(profileId) && Boolean(campaignId) && streamRange.isValid,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
